import { create } from 'zustand';
import { appendTabStripEntry } from '../utils/workspaceTabPersistence';
import { clearDbQueryResultCache } from './dbQueryResultCache';
import { clearDbFilterDrafts } from './dbFilterDrafts';
import { docWorkspaceKey } from './docWorkspaceKey';

/**
 * Open editor tabs (request / collection / schema), their strip order, the
 * active tab per type, and a few tab-related UI nonces.
 *
 * Replaces the App-level useState plus the five ref-mirrors (requestTabsRef,
 * schemaTabsRef, activeRequestTabIdRef, activeSchemaTabIdRef, activeTabTypeRef)
 * that let async handlers read the latest tabs synchronously —
 * `useTabsStore.getState()` is the synchronous equivalent. (tabsHydratingRef is
 * a separate imperative flag, not a state mirror, and stays a ref in App.)
 *
 * Setters are exported as stable module-level functions, named identically to
 * the old useState setters, and mirror useState semantics (value or updater).
 */
const initialState = {
  requestTabs: [],
  collectionTabs: [],
  schemaTabs: [],
  dbManagerTabs: [],
  taskManagerTabs: [],
  projectsTabs: [],
  documentTabs: [],
  tabStripOrder: [],
  activeRequestTabId: null,
  activeCollectionTabId: null,
  activeSchemaTabId: null,
  activeDbManagerTabId: null,
  activeTaskManagerTabId: null,
  activeProjectsTabId: null,
  activeDocumentTabId: null,
  activeTabType: 'request', // 'request' | 'collection' | 'schema' | 'db-manager-table' | 'task-manager-board' | 'projects-project' | 'document-editor'
  requestTabSyncRevision: 0,
  tabsHydrating: false,
  urlFocusNonce: 0,
  // "+" in the DB Manager tab strip opens a database picker (host list +
  // databases of each live connection) before creating the query tab.
  dbQueryPickerOpen: false,
  // Ctrl+S on an unnamed query tab opens the save-script name modal for it.
  dbScriptSaveRequest: null, // { tabId } | null
  // Set while a "you have unsaved grid edits" prompt is up; `proceed` re-runs
  // whatever navigation asked for it once the user accepts losing them.
  dbTabLeaveGuard: null, // { proceed: () => void } | null
};

const make = (set, key) => (value) =>
  set((state) => ({ [key]: typeof value === 'function' ? value(state[key]) : value }));

export const useTabsStore = create((set) => ({
  ...initialState,
  setRequestTabs: make(set, 'requestTabs'),
  setCollectionTabs: make(set, 'collectionTabs'),
  setSchemaTabs: make(set, 'schemaTabs'),
  setDbManagerTabs: make(set, 'dbManagerTabs'),
  setTaskManagerTabs: make(set, 'taskManagerTabs'),
  setProjectsTabs: make(set, 'projectsTabs'),
  setDocumentTabs: make(set, 'documentTabs'),
  setTabStripOrder: make(set, 'tabStripOrder'),
  setActiveRequestTabId: make(set, 'activeRequestTabId'),
  setActiveCollectionTabId: make(set, 'activeCollectionTabId'),
  setActiveSchemaTabId: make(set, 'activeSchemaTabId'),
  setActiveDbManagerTabId: make(set, 'activeDbManagerTabId'),
  setActiveTaskManagerTabId: make(set, 'activeTaskManagerTabId'),
  setActiveProjectsTabId: make(set, 'activeProjectsTabId'),
  setActiveDocumentTabId: make(set, 'activeDocumentTabId'),
  setActiveTabType: make(set, 'activeTabType'),
  setRequestTabSyncRevision: make(set, 'requestTabSyncRevision'),
  setTabsHydrating: make(set, 'tabsHydrating'),
  setUrlFocusNonce: make(set, 'urlFocusNonce'),
  setDbQueryPickerOpen: make(set, 'dbQueryPickerOpen'),
  setDbScriptSaveRequest: make(set, 'dbScriptSaveRequest'),
  setDbTabLeaveGuard: make(set, 'dbTabLeaveGuard'),
  reset: () => {
    clearDbQueryResultCache(); // logout must not retain another session's query results
    clearDbFilterDrafts();
    set({ ...initialState });
  },
}));

export function setDbQueryPickerOpen(open) {
  useTabsStore.getState().setDbQueryPickerOpen(open);
}

export function setDbScriptSaveRequest(request) {
  useTabsStore.getState().setDbScriptSaveRequest(request);
}

export function setDbTabLeaveGuard(guard) {
  useTabsStore.getState().setDbTabLeaveGuard(guard);
}

/**
 * Call before anything that navigates away from — or destroys — the active DB
 * Manager tab. Returns true when the tab has staged grid edits, in which case
 * the caller must abort: a prompt is raised, and `proceed` re-runs the caller
 * once the user accepts discarding them.
 *
 * Why a prompt rather than keeping the edits: pending-edit keys are row indexes
 * into the CURRENT result set, and arriving on a tab refetches it. Preserving
 * them across a switch would mean holding every tab's rows in memory and
 * skipping that refetch, which widens the window in which the row a staged
 * WHERE clause addresses has changed underneath it. Losing the work loudly
 * beats writing it to the wrong row quietly.
 *
 * Query tabs are exempt: their SQL draft is committed to the tab record on
 * every keystroke, so switching away loses nothing.
 */
export function guardDirtyDbTab(proceed) {
  const state = useTabsStore.getState();
  if (state.activeTabType !== 'db-manager-table') return false;
  const current = state.dbManagerTabs.find((t) => t.id === state.activeDbManagerTabId);
  if (!current || !current.tableName || !current.hasUnsavedChanges) return false;
  state.setDbTabLeaveGuard({ proceed });
  return true;
}

/** Active tab of each kind (replaces the App-level memos). */
export function selectCurrentRequestTab(state) {
  return state.requestTabs.find((t) => t.id === state.activeRequestTabId) || null;
}
export function selectCurrentCollectionTab(state) {
  return state.collectionTabs.find((t) => t.id === state.activeCollectionTabId) || null;
}
export function selectCurrentSchemaTab(state) {
  return state.schemaTabs.find((t) => t.id === state.activeSchemaTabId) || null;
}
export function selectCurrentDbManagerTab(state) {
  return state.dbManagerTabs.find((t) => t.id === state.activeDbManagerTabId) || null;
}
export function selectCurrentTaskManagerTab(state) {
  return state.taskManagerTabs.find((t) => t.id === state.activeTaskManagerTabId) || null;
}
export function selectCurrentProjectsTab(state) {
  return state.projectsTabs.find((t) => t.id === state.activeProjectsTabId) || null;
}
export function selectCurrentDocumentTab(state) {
  return state.documentTabs.find((t) => t.id === state.activeDocumentTabId) || null;
}

// Stable module-level setters (delegate to the store actions).
export const setRequestTabs = (v) => useTabsStore.getState().setRequestTabs(v);
export const setCollectionTabs = (v) => useTabsStore.getState().setCollectionTabs(v);
export const setSchemaTabs = (v) => useTabsStore.getState().setSchemaTabs(v);
export const setDbManagerTabs = (v) => useTabsStore.getState().setDbManagerTabs(v);
export const setTaskManagerTabs = (v) => useTabsStore.getState().setTaskManagerTabs(v);
export const setProjectsTabs = (v) => useTabsStore.getState().setProjectsTabs(v);
export const setDocumentTabs = (v) => useTabsStore.getState().setDocumentTabs(v);
export const setTabStripOrder = (v) => useTabsStore.getState().setTabStripOrder(v);
export const setActiveRequestTabId = (v) => useTabsStore.getState().setActiveRequestTabId(v);
export const setActiveCollectionTabId = (v) => useTabsStore.getState().setActiveCollectionTabId(v);
export const setActiveSchemaTabId = (v) => useTabsStore.getState().setActiveSchemaTabId(v);
export const setActiveDbManagerTabId = (v) => useTabsStore.getState().setActiveDbManagerTabId(v);
export const setActiveTaskManagerTabId = (v) => useTabsStore.getState().setActiveTaskManagerTabId(v);
export const setActiveProjectsTabId = (v) => useTabsStore.getState().setActiveProjectsTabId(v);
export const setActiveDocumentTabId = (v) => useTabsStore.getState().setActiveDocumentTabId(v);
export const setActiveTabType = (v) => useTabsStore.getState().setActiveTabType(v);
export const setRequestTabSyncRevision = (v) => useTabsStore.getState().setRequestTabSyncRevision(v);
export const setTabsHydrating = (v) => useTabsStore.getState().setTabsHydrating(v);
export const setUrlFocusNonce = (v) => useTabsStore.getState().setUrlFocusNonce(v);

function dbManagerTabKey({ extensionId, connId, dbName, tableName }) {
  return `db-manager-table:${extensionId}:${connId}:${dbName}:${tableName}`;
}

/**
 * A key for the Nth copy of the same table.
 *
 * The base key is deterministic on purpose — that is what makes re-opening a
 * table from the tree focus the tab you already have. Deliberately opening a
 * second copy therefore needs a distinct key, and the suffix is what provides
 * it. Nothing parses these ids (they are only ever compared), so appending is
 * safe.
 */
function freeDbManagerTabKey(descriptor, existingTabs) {
  const base = dbManagerTabKey(descriptor);
  const taken = new Set(existingTabs.map((t) => t.id));
  if (!taken.has(base)) return base;
  for (let n = 2; n < 500; n += 1) {
    const candidate = `${base}#${n}`;
    if (!taken.has(candidate)) return candidate;
  }
  // Far past anything deliberate; a timestamp still beats refusing to open.
  return `${base}#${Date.now()}`;
}

/**
 * Opens (or focuses an existing) host-rendered results tab for a DB Manager
 * table. Called directly from hostBridge.js (no React hook context there),
 * mirroring how it already calls useExtensionStore.getState().openModal(...)
 * for the `ui.openModal` capability.
 */
export function openDbManagerTab({ extensionId, connId, engine, dbName, tableName, label, pendingAction, filterText, newTab }) {
  const state0 = useTabsStore.getState();
  // `newTab` is "open ANOTHER one", not "open it": the deterministic key is
  // what makes an ordinary open focus the tab that already exists.
  const id = newTab
    ? freeDbManagerTabKey({ extensionId, connId, dbName, tableName }, state0.dbManagerTabs)
    : dbManagerTabKey({ extensionId, connId, dbName, tableName });
  // Opening a table navigates away from the active one. Guard before mutating
  // anything, so the retry starts from an unchanged store.
  if (
    id !== useTabsStore.getState().activeDbManagerTabId &&
    guardDirtyDbTab(() =>
      openDbManagerTab({ extensionId, connId, engine, dbName, tableName, label, pendingAction, filterText, newTab })
    )
  ) {
    return null;
  }
  const state = useTabsStore.getState();
  // Re-opening a table from the sidebar tree only happens through a live
  // connection — that's the explicit user action that un-flags a restored
  // tab (the flag otherwise survives background auto-connects on purpose).
  clearDbManagerTabNeedsReconnect(id);
  if (!state.dbManagerTabs.some((t) => t.id === id)) {
    const newTab = {
      id,
      type: 'db-manager-table',
      extensionId,
      connId,
      engine: engine || 'mysql',
      dbName,
      tableName,
      label: label || tableName,
      // Foreign-key navigation opens the target already narrowed to the row
      // that was pointed at.
      filterText: filterText || '',
      // Transient one-shot ('export' | 'import'): DbManagerResultsTab
      // consumes and clears it once the tab's first result has loaded.
      // pendingActionAt lets the consumer drop actions that went stale
      // (load failed, user navigated away) instead of firing them later.
      pendingAction: pendingAction || undefined,
      pendingActionAt: pendingAction ? Date.now() : undefined,
    };
    setDbManagerTabs((prev) => [...prev, newTab]);
    setTabStripOrder((prev) => appendTabStripEntry(prev, { type: 'db-manager-table', id }));
  } else if (pendingAction || filterText !== undefined) {
    // Tab already open: re-arm the pending action, and/or replace its filter.
    // filterNonce is what makes the results tab re-run when the id has not
    // changed — a self-referencing foreign key navigates to the tab already on
    // screen, so there is no tab switch to trigger the refetch. It is
    // deliberately absent from the snapshot serializer: it means "re-run now",
    // which is meaningless to restore.
    setDbManagerTabs((prev) =>
      prev.map((t) =>
        t.id === id
          ? {
              ...t,
              ...(pendingAction ? { pendingAction, pendingActionAt: Date.now() } : {}),
              ...(filterText !== undefined ? { filterText, filterNonce: (t.filterNonce || 0) + 1 } : {}),
            }
          : t
      )
    );
  }
  setActiveDbManagerTabId(id);
  setActiveTabType('db-manager-table');
  return id;
}

/**
 * Clears the `needsReconnect` flag on one restored DB Manager tab — called
 * only on an explicit user action for that tab (its Connect button, or
 * re-opening its table from the sidebar tree). A background auto-connect by
 * the sidebar extension never clears these; each restored tab keeps its
 * Connect screen until the user acts on it.
 */
/**
 * Flags one DB Manager tab as needing reconnection — used when a query fails
 * with a connection-level error (server died, pool closed) so the tab falls
 * back to its reconnect screen instead of dead-ending in error text.
 */
export function setDbManagerTabNeedsReconnect(tabId) {
  setDbManagerTabs((prev) =>
    prev.some((t) => t.id === tabId && !t.needsReconnect)
      ? prev.map((t) => (t.id === tabId ? { ...t, needsReconnect: true } : t))
      : prev
  );
}

export function clearDbManagerTabNeedsReconnect(tabId) {
  setDbManagerTabs((prev) => {
    const target = prev.find((t) => t.id === tabId && t.needsReconnect);
    if (!target) return prev;
    return prev.map((t) => {
      if (t.id !== tabId) return t;
      const { needsReconnect, ...rest } = t;
      return rest;
    });
  });
}

/** Clears the one-shot AI auto-run flags so a query tab only auto-runs once. */
export function clearDbManagerTabAutoRun(tabId) {
  setDbManagerTabs((prev) => {
    const target = prev.find((t) => t.id === tabId && t.autoRun);
    if (!target) return prev;
    return prev.map((t) => {
      if (t.id !== tabId) return t;
      const { autoRun, aiRunId, ...rest } = t;
      return rest;
    });
  });
}

/**
 * Opens a new blank SQL query tab for the given connection (no table bound
 * to it), used by the tab strip's "+" button when the DB Manager panel is
 * the active tab type instead of falling through to a blank request tab.
 */
export function openDbManagerQueryTab({ extensionId, connId, engine, dbName, sql = '', scriptName = null, scriptLinked = true, autoRun = false, aiRunId = null, label: customLabel = null }) {
  if (
    guardDirtyDbTab(() =>
      openDbManagerQueryTab({ extensionId, connId, engine, dbName, sql, scriptName, scriptLinked, autoRun, aiRunId, label: customLabel })
    )
  ) {
    return null;
  }
  const state = useTabsStore.getState();
  const querySeq = state.dbManagerTabs.filter((t) => !t.tableName && t.view !== 'overview').length + 1;
  const id = `db-manager-query:${extensionId}:${connId}:${Date.now()}`;
  // Strip label carries the script name (saved scripts) or the bound database
  // so parallel query tabs are tellable apart at a glance.
  // customLabel: an extension naming what it opened (e.g. 'DDL books').
  const label = customLabel
    || (scriptName
      ? (dbName ? `${scriptName} - "${dbName}"` : scriptName)
      : (dbName ? `Query - "${dbName}"` : `Query ${querySeq}`));
  const newTab = {
    id,
    type: 'db-manager-table',
    extensionId,
    connId,
    engine: engine || 'mysql',
    dbName: dbName || null,
    tableName: null,
    label,
    sql,
    scriptName,
    scriptLinked,
    // Baseline for the unsaved-dot: draft === savedSql means clean.
    savedSql: sql,
    // One-shot: the AI assistant opened this tab and wants the query auto-run.
    // DbManagerResultsTab runs it once, resolves the AI bridge with result
    // metadata (never rows), and clears these flags.
    autoRun,
    aiRunId,
  };
  setDbManagerTabs((prev) => [...prev, newTab]);
  setTabStripOrder((prev) => appendTabStripEntry(prev, { type: 'db-manager-table', id }));
  setActiveDbManagerTabId(id);
  setActiveTabType('db-manager-table');
  return id;
}

/**
 * Open a second copy of an existing DB Manager tab.
 *
 * What gets copied is the VIEW — the table (or the SQL), the filter, the sort,
 * the column widths and which columns are hidden. What does not is anything
 * uncommitted: staged edits and pending rows are keyed by row index into a
 * result set the copy has not fetched yet, so carrying them across would point
 * them at rows that do not exist.
 *
 * The copy is inserted straight after the original rather than at the end,
 * because the reason to make one is to compare it with the tab it came from.
 */
export function duplicateDbManagerTab(tabId) {
  const state = useTabsStore.getState();
  const source = state.dbManagerTabs.find((t) => t.id === tabId);
  if (!source) return null;

  const id = source.tableName
    ? freeDbManagerTabKey(
        {
          extensionId: source.extensionId,
          connId: source.connId,
          dbName: source.dbName,
          tableName: source.tableName,
        },
        state.dbManagerTabs
      )
    : `db-manager-query:${source.extensionId}:${source.connId}:${Date.now()}`;

  const copy = {
    ...source,
    id,
    // A duplicated query tab is a scratch copy, not a second window onto the
    // same saved script — linking both to one file would make each one's save
    // silently overwrite the other's.
    scriptName: source.tableName ? source.scriptName : undefined,
    scriptLinked: source.tableName ? source.scriptLinked : false,
    // One-shot flags belong to the open that armed them.
    pendingAction: undefined,
    pendingActionAt: undefined,
    autoRun: undefined,
    aiRunId: undefined,
    // A copy has nothing staged, by definition.
    needsReconnect: source.needsReconnect,
  };

  setDbManagerTabs((prev) => {
    const at = prev.findIndex((t) => t.id === tabId);
    const next = [...prev];
    next.splice(at === -1 ? prev.length : at + 1, 0, copy);
    return next;
  });
  setTabStripOrder((prev) => appendTabStripEntry(prev, { type: 'db-manager-table', id }));
  setActiveDbManagerTabId(id);
  setActiveTabType('db-manager-table');
  return id;
}

const TASK_MANAGER_HOME_TAB_ID = 'task-manager-board:home';

function taskManagerTabKey(boardId) {
  return boardId ? `task-manager-board:${boardId}` : TASK_MANAGER_HOME_TAB_ID;
}

/**
 * Opens (or focuses an existing) host-rendered tab for the Task Manager
 * extension — either the pinned "Boards" grid (boardId omitted/null) or one
 * open board (boardId set). Deterministic id per boardId, same dedup
 * behavior as openDbManagerTab: reopening a board focuses its existing tab
 * instead of duplicating it.
 */
export function openTaskManagerBoardTab({ boardId = null, label } = {}) {
  // The pinned "Boards" home tab must always exist, wherever a board is opened from.
  if (boardId) ensureTaskManagerHomeTab();
  const id = taskManagerTabKey(boardId);
  const state = useTabsStore.getState();
  if (!state.taskManagerTabs.some((t) => t.id === id)) {
    const newTab = {
      id,
      type: 'task-manager-board',
      boardId,
      label: label || (boardId ? 'Board' : 'Boards'),
      pinned: !boardId,
    };
    // The pinned "Boards" home tab always sits first; board tabs append.
    setTaskManagerTabs((prev) => (boardId ? [...prev, newTab] : [newTab, ...prev]));
    setTabStripOrder((prev) =>
      boardId
        ? appendTabStripEntry(prev, { type: 'task-manager-board', id })
        : [{ type: 'task-manager-board', id }, ...prev]
    );
  }
  setActiveTaskManagerTabId(id);
  setActiveTabType('task-manager-board');
  return id;
}

/**
 * Ensures the pinned "Boards" tab exists, without changing which tab is
 * currently active — used when entering the Task Manager panel so
 * EditorPane never renders with zero tabs, but without stealing focus from
 * whatever board tab (if any) was already active.
 */
export function ensureTaskManagerHomeTab() {
  const state = useTabsStore.getState();
  if (state.taskManagerTabs.some((t) => t.id === TASK_MANAGER_HOME_TAB_ID)) return;
  const newTab = { id: TASK_MANAGER_HOME_TAB_ID, type: 'task-manager-board', boardId: null, label: 'Boards', pinned: true };
  setTaskManagerTabs((prev) => [newTab, ...prev]);
  setTabStripOrder((prev) => [{ type: 'task-manager-board', id: TASK_MANAGER_HOME_TAB_ID }, ...prev]);
  if (!state.activeTaskManagerTabId) setActiveTaskManagerTabId(TASK_MANAGER_HOME_TAB_ID);
}

// ── Documents extension tabs ────────────────────────────────────────────────

function documentTabKey(documentId) {
  return `document-editor:${documentId}`;
}

/**
 * Opens (or focuses an existing) host-rendered tab for one document. Mirrors
 * openTaskManagerBoardTab: a deterministic id per documentId dedups so
 * re-opening a document from the tree focuses its existing tab instead of
 * duplicating it. Unlike Task Manager there is no pinned "home" tab — the
 * folder tree lives in the panel's sidebar, not in a tab.
 *
 * `scope` is the documents-store scope descriptor the document lives in —
 * `{ type: 'personal' }` (default, omitted by every call site that predates
 * team documents) or `{ type: 'team', teamId }`. It's carried on the tab
 * object (not re-derived from documentId, which is opaque) so
 * DocumentEditorTab can thread the right scope into every store call for the
 * lifetime of the tab. A re-open of an already-open tab focuses it as-is —
 * its original scope is never overwritten by a later call with a different
 * scope (moving a document between scopes is a distinct operation, not a
 * side effect of opening it).
 */
export function openDocumentTab({ documentId, label, scope } = {}) {
  if (!documentId) return null;
  const id = documentTabKey(documentId);
  const state = useTabsStore.getState();
  if (!state.documentTabs.some((t) => t.id === id)) {
    const newTab = {
      id,
      type: 'document-editor',
      documentId,
      label: label || 'Untitled',
      scope: scope || { type: 'personal' },
    };
    setDocumentTabs((prev) => [...prev, newTab]);
    setTabStripOrder((prev) => appendTabStripEntry(prev, { type: 'document-editor', id }));
  }
  setActiveDocumentTabId(id);
  setActiveTabType('document-editor');
  return id;
}

/** Keeps an open document tab's label in sync with its title (rename/first save). */
export function updateDocumentTabLabel(documentId, label) {
  const id = documentTabKey(documentId);
  setDocumentTabs((prev) => prev.map((t) => (t.id === id ? { ...t, label: label || 'Untitled' } : t)));
}

/**
 * Points an open document tab at the scope its document lives in now — it was
 * moved between the cloud and this device (cloud/local items) after the tab or
 * link was created. Saves then go to the right place.
 */
export function updateDocumentTabScope(documentId, scope) {
  const id = documentTabKey(documentId);
  setDocumentTabs((prev) => prev.map((t) => (t.id === id ? { ...t, scope } : t)));
}

/** Keeps an open board tab's strip label in sync with a rename. */
export function updateTaskManagerBoardTabLabel(boardId, label) {
  setTaskManagerTabs((prev) => prev.map((t) => (t.boardId === boardId ? { ...t, label: label || 'Board' } : t)));
}

/** Drives the tab's unsaved dot (same indicator as request tabs) while a save is pending. */
export function setDocumentTabUnsaved(documentId, unsaved) {
  const id = documentTabKey(documentId);
  setDocumentTabs((prev) =>
    prev.map((t) => (t.id === id ? { ...t, hasUnsavedChanges: !!unsaved } : t))
  );
}

/**
 * Closes a document's tab (if open) — used when the document itself is
 * deleted, so a stale tab for now-nonexistent data doesn't linger. Falls back
 * to the next remaining document tab if the closed one was active.
 */
export function closeDocumentTab(documentId) {
  const id = documentTabKey(documentId);
  const state = useTabsStore.getState();
  if (!state.documentTabs.some((t) => t.id === id)) return;
  const newTabs = state.documentTabs.filter((t) => t.id !== id);
  setDocumentTabs(newTabs);
  setTabStripOrder((prev) => prev.filter((e) => !(e.type === 'document-editor' && e.id === id)));
  if (state.activeDocumentTabId === id) {
    // The neighbour comes from the same Documents workspace — a tab of another
    // one would switch the sidebar away (see documentTabWorkspaces.js).
    const key = docWorkspaceKey(state.documentTabs.find((t) => t.id === id)?.scope);
    const mine = newTabs.filter((t) => docWorkspaceKey(t.scope) === key);
    setActiveDocumentTabId(mine.length ? mine[mine.length - 1].id : null);
  }
}

/**
 * Closes a board's tab (if open) — used when the board itself is deleted, so
 * a stale tab for now-nonexistent data doesn't linger. Falls back to the
 * pinned "Boards" tab if the closed tab was active.
 */
export function closeTaskManagerBoardTab(boardId) {
  const id = taskManagerTabKey(boardId);
  const state = useTabsStore.getState();
  if (!state.taskManagerTabs.some((t) => t.id === id)) return;
  const newTabs = state.taskManagerTabs.filter((t) => t.id !== id);
  setTaskManagerTabs(newTabs);
  setTabStripOrder((prev) => prev.filter((e) => !(e.type === 'task-manager-board' && e.id === id)));
  if (state.activeTaskManagerTabId === id) {
    const pinned = newTabs.find((t) => t.pinned);
    setActiveTaskManagerTabId(pinned ? pinned.id : null);
  }
}

// ── Projects tabs (Graph & Projects, 13.09.2026) ─────────────────────────────
// The Boards shape exactly (owner: "open like the boards do"): a pinned
// "Projects" home tab with the card grid, and one tab per opened project.
// Session-lived like board tabs (not part of the per-workspace snapshot).

export const PROJECTS_HOME_TAB_ID = 'projects-project:home';

function projectsTabKey(projectId) {
  return projectId ? `projects-project:${projectId}` : PROJECTS_HOME_TAB_ID;
}

/** Ensures the pinned "Projects" tab exists without stealing focus. */
export function ensureProjectsHomeTab() {
  const state = useTabsStore.getState();
  if (state.projectsTabs.some((t) => t.id === PROJECTS_HOME_TAB_ID)) return;
  const home = { id: PROJECTS_HOME_TAB_ID, type: 'projects-project', projectId: null, label: 'Projects', pinned: true };
  setProjectsTabs((prev) => [home, ...prev]);
  setTabStripOrder((prev) => [{ type: 'projects-project', id: PROJECTS_HOME_TAB_ID }, ...prev]);
  if (!state.activeProjectsTabId) setActiveProjectsTabId(PROJECTS_HOME_TAB_ID);
}

/** Opens (or focuses) the tab for one project; no id = the pinned home tab. */
export function openProjectsProjectTab({ projectId = null, label } = {}) {
  ensureProjectsHomeTab();
  const id = projectsTabKey(projectId);
  if (projectId && !useTabsStore.getState().projectsTabs.some((t) => t.id === id)) {
    const tab = { id, type: 'projects-project', projectId, label: label || 'Project', pinned: false };
    setProjectsTabs((prev) => [...prev, tab]);
    setTabStripOrder((prev) => appendTabStripEntry(prev, { type: 'projects-project', id }));
  }
  setActiveProjectsTabId(id);
  setActiveTabType('projects-project');
  return id;
}

export function updateProjectsTabLabel(projectId, label) {
  const id = projectsTabKey(projectId);
  setProjectsTabs((prev) => prev.map((t) => (t.id === id ? { ...t, label: label || 'Project' } : t)));
}

/** Closes a deleted project's tab; falls back to the pinned "Projects" tab. */
export function closeProjectsProjectTab(projectId) {
  const id = projectsTabKey(projectId);
  const state = useTabsStore.getState();
  if (!state.projectsTabs.some((t) => t.id === id)) return;
  const next = state.projectsTabs.filter((t) => t.id !== id);
  setProjectsTabs(next);
  setTabStripOrder((prev) => prev.filter((e) => !(e.type === 'projects-project' && e.id === id)));
  if (state.activeProjectsTabId === id) {
    const pinned = next.find((t) => t.pinned);
    setActiveProjectsTabId(pinned ? pinned.id : null);
  }
}
